/**
 * userService.js
 * User-related Firestore operations
 */

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc,
  addDoc, query, or, where, orderBy, onSnapshot, serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { COLLECTIONS } from './collections';
import { getRoles } from '../../utils/roles';

/**
 * Get all users (one-time)
 */
export async function getAllUsers() {
  const snap = await getDocs(collection(db, COLLECTIONS.USERS));
  return snap.docs.map(d => ({ key: d.id, ...d.data() }));
}

/**
 * Get all students (one-time)
 */
export async function getAllStudents() {
  const q = query(
    collection(db, COLLECTIONS.USERS),
    or(
      where('roles', 'array-contains', 'student'),
      where('role', 'array-contains', 'student'),
      where('role', '==', 'student'),
      where('roles', '==', 'student')
    )
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ ...d.data(), key: d.id })).filter(user => getRoles(user).includes('student'));
}

/**
 * Get all teachers
 */
export async function getAllTeachers() {
  const q = query(
    collection(db, COLLECTIONS.USERS),
    or(
      where('roles', 'array-contains', 'teacher'),
      where('role', 'array-contains', 'teacher'),
      where('role', '==', 'teacher'),
      where('roles', '==', 'teacher')
    )
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ ...d.data(), key: d.id })).filter(user => getRoles(user).includes('teacher'));
}

/**
 * Get user by ID
 */
export async function getUserById(userId) {
  const snap = await getDoc(doc(db, COLLECTIONS.USERS, userId));
  return snap.exists() ? { key: snap.id, ...snap.data() } : null;
}

/**
 * Create or update user
 */
export async function upsertUser(userId, data) {
  await setDoc(doc(db, COLLECTIONS.USERS, userId), {
    ...data,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

/**
 * Update user profile
 */
export async function updateUserProfile(userId, data) {
  await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
    ...data,
    updatedAt: serverTimestamp()
  });
}

/**
 * Get users by class
 */
export async function getUsersByClass(classId) {
  const q = query(
    collection(db, COLLECTIONS.USERS),
    where('classId', '==', classId)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ key: d.id, ...d.data() }));
}

/**
 * Get users by multiple IDs (batch)
 */
export async function getUsersByIds(userIds) {
  if (!userIds || userIds.length === 0) return [];
  const results = await Promise.all(
    userIds.map(id => getUserById(id))
  );
  return results.filter(Boolean);
}

export default {
  getAllUsers,
  getAllStudents,
  getAllTeachers,
  getUserById,
  upsertUser,
  updateUserProfile,
  getUsersByClass,
  getUsersByIds,
};
